import type { ReactNode, ThHTMLAttributes } from 'react';

import { ArrowUpDown, ChevronDown, ChevronUp } from 'lucide-react';

import { cx } from '@/lib/cx';

import styles from './Table.module.css';

export type SortDirection = 'asc' | 'desc';

export function TableWrap({ children }: { children: ReactNode }) {
  // Horizontal scrolling belongs to the table, never to the page body.
  return <div className={styles.wrap}>{children}</div>;
}

export function Table({ children, minWidth }: { children: ReactNode; minWidth?: number }) {
  return (
    <table className={styles.table} style={minWidth ? { minInlineSize: minWidth } : undefined}>
      {children}
    </table>
  );
}

export type SortableHeaderProps = ThHTMLAttributes<HTMLTableCellElement> & {
  label: string;
  /** Custom content: a select-all checkbox, for instance. Replaces the label. */
  children?: ReactNode;
  /** Omit to render a plain, non-sortable header. */
  sortKey?: string;
  activeKey?: string;
  direction?: SortDirection;
  onSort?: (key: string) => void;
};

export function Th({
  label,
  children,
  sortKey,
  activeKey,
  direction = 'asc',
  onSort,
  className,
  ...rest
}: SortableHeaderProps) {
  if (children) {
    return (
      <th {...rest} className={cx(styles.th, className)} scope="col">
        {children}
      </th>
    );
  }

  if (!sortKey || !onSort) {
    return (
      <th {...rest} className={cx(styles.th, className)} scope="col">
        {label}
      </th>
    );
  }

  const active = activeKey === sortKey;
  const nextDirection: SortDirection = active && direction === 'asc' ? 'desc' : 'asc';

  return (
    <th
      {...rest}
      className={cx(styles.th, styles.sortable, className)}
      scope="col"
      // Read out by screen readers as "sorted ascending", so the current order is
      // available without seeing the arrow.
      aria-sort={active ? (direction === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button
        type="button"
        className={styles.sortButton}
        onClick={() => {
          onSort(sortKey);
        }}
        aria-label={`Sort by ${label}, ${nextDirection === 'asc' ? 'ascending' : 'descending'}`}
      >
        {label}
        {active ? (
          direction === 'asc' ? (
            <ChevronUp size={12} />
          ) : (
            <ChevronDown size={12} />
          )
        ) : (
          <ArrowUpDown size={12} className={styles.idleSort} />
        )}
      </button>
    </th>
  );
}

export function Td({
  children,
  className,
  ...rest
}: {
  children?: ReactNode;
  className?: string | undefined;
} & ThHTMLAttributes<HTMLTableCellElement>) {
  return (
    <td {...rest} className={cx(styles.td, className)}>
      {children}
    </td>
  );
}

export function Tr({
  children,
  selected,
  onClick,
  className,
}: {
  children: ReactNode;
  selected?: boolean;
  onClick?: () => void;
  className?: string | undefined;
}) {
  if (!onClick) {
    return <tr className={cx(selected && styles.selected, className)}>{children}</tr>;
  }

  return (
    // A clickable row is still reachable by keyboard. The row is not a button (a
    // button cannot contain cells), so the role and the key handler are explicit.
    <tr
      className={cx(styles.clickable, selected && styles.selected, className)}
      onClick={onClick}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onClick();
        }
      }}
      tabIndex={0}
      role="button"
    >
      {children}
    </tr>
  );
}
